import React from 'react'
import PropTypes from 'prop-types'
import { Box, TableCell, TableRow, TextField, Typography } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'

import { forEach, isEmpty, cloneDeep } from 'lodash'

import SectionTable from './components/SectionTable'
import { questionsSection } from './constants'

const useStyles = makeStyles((theme) => {
  return {
    sectionRow: {
      height: 55,
    },
    inputWrapper: {
      display: 'flex',
      alignItems: 'center',
    },
    inputStyle: {
      width: '73px',
    },
    subLabel: {
      color: theme.palette.text.secondary,
    },
    displayString: {
      paddingRight: theme.spacing(13),
    },
    dashSpacing: {
      margin: `0 ${theme.spacing(1)}px`,
    },
    danger: {
      color: theme.palette.error.main,
    },
  }
})

const getId = (sectionId, firstYearConfig, m) => `${sectionId}-${firstYearConfig ? 'first-year' : 'transfer'}-${m}`
const checkForVal = (val) => (val == null || val === undefined ? false : true)

const SupplementalSection = ({ isEditing, applicationConfig, setApplicationConfig, loading }) => {
  const firstYearConfig = applicationConfig.firstYearConfiguration
  const transferConfig = applicationConfig.transferConfiguration
  const classes = useStyles()

  const onSupplementalSectionChange = (event, sectionId, isFirstYear, isMin) => {
    const invalidChars = ['-', '+', 'e', '.', 'π']
    const newVal = parseInt(event.target.value)
    if (newVal < 0 || newVal > 999 || invalidChars.includes(event.nativeEvent.data)) return

    const updatedState = cloneDeep(applicationConfig)

    if (isFirstYear) {
      const firstYearQsConfig = updatedState.firstYearConfiguration.questionsSection
        ? updatedState.firstYearConfiguration.questionsSection
        : {}
      let sectionConfig = firstYearQsConfig[sectionId] ? firstYearQsConfig[sectionId] : {}
      if (isNaN(newVal) || newVal === '') sectionConfig = null
      else if (isMin) sectionConfig.min = newVal
      else sectionConfig.max = newVal

      firstYearQsConfig[sectionId] = sectionConfig == null ? null : { ...sectionConfig }
      updatedState.firstYearConfiguration.questionsSection = { ...firstYearQsConfig }
    } else {
      const transferQsConfig = updatedState.transferConfiguration.questionsSection
        ? updatedState.transferConfiguration.questionsSection
        : {}
      let sectionConfig = transferQsConfig[sectionId] ? transferQsConfig[sectionId] : {}
      if (isNaN(newVal) || newVal === '') sectionConfig = null
      else if (isMin) sectionConfig.min = newVal
      else sectionConfig.max = newVal

      transferQsConfig[sectionId] = sectionConfig == null ? null : { ...sectionConfig }
      updatedState.transferConfiguration.questionsSection = { ...transferQsConfig }
    }

    const errors = validateForm(updatedState)

    setApplicationConfig(updatedState, errors.length > 0 ? true : false)
  }

  const validateForm = (cfg) => {
    const errors = []
    const fyConfig = { ...cfg.firstYearConfiguration, topLevelSection: 'first-year' }
    const tConfig = { ...cfg.transferConfiguration, topLevelSection: 'transfer' }

    forEach([fyConfig, tConfig], (config) => {
      const topLevelSection = config.topLevelSection

      forEach(questionsSection, (section) => {
        const qSection = config.questionsSection ? config.questionsSection : {}
        const part = qSection[section.id] ? qSection[section.id] : null
        if (part != null) {
          if (checkForVal(part.min) && !checkForVal(part.max)) {
            errors.push({ id: `${section.id}-${topLevelSection}-max`, msg: 'Must enter a max' })
          } else if (checkForVal(part.max) && !checkForVal(part.min)) {
            errors.push({ id: `${section.id}-${topLevelSection}-min`, msg: 'Must enter a min' })
          } else if (part.min > part.max) {
            errors.push({ id: `${section.id}-${topLevelSection}-min`, msg: 'Min must be less than max' })
            errors.push({ id: `${section.id}-${topLevelSection}-max`, msg: 'Min must be less than max' })
          }
        }
      })
    })
    return errors
  }

  const getStringValue = (minVal, maxVal) => {
    return minVal !== '' && maxVal !== '' ? (
      <Typography
        variant="body1"
        className={classes.displayString}
      >
        {`${minVal} - ${maxVal}`}
      </Typography>
    ) : (
      <Typography className={classes.displayString}>None</Typography>
    )
  }

  const formErrors = validateForm(applicationConfig)

  const renderSupplementalSectionConfig = (section, isFirstYear = false) => {
    const config = isFirstYear ? firstYearConfig : transferConfig
    const isEnabled = config && config.enabled

    const sectionConfig =
      config.questionsSection && config.questionsSection[section.id] && !isEmpty(config.questionsSection[section.id])
        ? config.questionsSection[section.id]
        : {}
    const minVal = sectionConfig.min ?? ''
    const maxVal = sectionConfig.max ?? ''

    const minId = getId(section.id, isFirstYear, 'min')
    const maxId = getId(section.id, isFirstYear, 'max')

    return isEditing ? (
      <Box className={classes.inputWrapper}>
        <Box>
          <TextField
            value={minVal}
            onChange={(e) => onSupplementalSectionChange(e, section.id, isFirstYear, true)}
            disabled={!isEnabled}
            variant="outlined"
            size="small"
            type="number"
            label="Min"
            className={classes.inputStyle}
            id={minId}
            error={formErrors.find((e) => e.id === minId) ? true : false}
          />
        </Box>

        <Typography className={classes.dashSpacing}>-</Typography>

        <Box>
          <TextField
            value={maxVal}
            onChange={(e) => onSupplementalSectionChange(e, section.id, isFirstYear, false)}
            disabled={!isEnabled}
            variant="outlined"
            size="small"
            type="number"
            label="Max"
            className={classes.inputStyle}
            id={maxId}
            error={formErrors.find((e) => e.id === maxId) ? true : false}
          />
        </Box>
      </Box>
    ) : (
      getStringValue(minVal, maxVal)
    )
  }

  return (
    <SectionTable sectionName="Supplement Questions">
      {questionsSection &&
        questionsSection.map((section) => {
          return (
            <TableRow
              data-testid="supplemental-section-row"
              key={section.id}
              className={classes.sectionRow}
            >
              <TableCell
                align="left"
                variant="body"
                component="th"
                size="small"
              >
                <Box>
                  <Typography variant="body1">{section.label}</Typography>
                  <Typography
                    variant="body2"
                    className={classes.subLabel}
                  >
                    {section.subLabel}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="right"
                size="small"
              >
                {loading && (
                  <Skeleton
                    data-testid="loading-skeleton-testid"
                    width={48}
                    height={18}
                  />
                )}
                {!loading && renderSupplementalSectionConfig(section, true)}
              </TableCell>
              <TableCell
                align="right"
                size="small"
              >
                {loading && (
                  <Skeleton
                    data-testid="loading-skeleton-testid"
                    width={48}
                    height={18}
                  />
                )}
                {!loading && renderSupplementalSectionConfig(section)}
              </TableCell>
            </TableRow>
          )
        })}
    </SectionTable>
  )
}

SupplementalSection.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  applicationConfig: PropTypes.object.isRequired,
  setApplicationConfig: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
}

export default SupplementalSection
